import { isDefined } from "@clipboard-health/util-ts";
import { type GetShiftResponse } from "@src/appV2/Shifts/Shift/api/useGetShift";
import {
  addDays,
  addMinutes,
  differenceInHours,
  differenceInMinutes,
  parseISO,
  setDate,
  setMonth,
  setYear,
} from "date-fns";

import { MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS } from "../../Shift/constants";

// Utility function to get default clock times for shift verification
// This function calculates default clock times based on geofence entry/exit and HCP clock in/out times.
export function getShiftVerificationDefaultClockTimes(
  shift: Pick<GetShiftResponse["response"], "geofence">
): {
  clockIn?: Date;
  clockOut?: Date;
  breakStart?: Date;
  breakEnd?: Date;
} {
  const geofenceEntry = isDefined(shift.geofence?.entry)
    ? parseISO(shift.geofence.entry || "")
    : undefined;
  const geofenceExit = isDefined(shift.geofence?.exit)
    ? parseISO(shift.geofence.exit || "")
    : undefined;

  const clockInTime = geofenceEntry ?? undefined;
  const clockOutTime = geofenceExit ?? undefined;

  let breakStartTime: Date | undefined;
  if (
    isDefined(clockInTime) &&
    isDefined(clockOutTime) &&
    differenceInHours(clockOutTime, clockInTime) >= MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS
  ) {
    const shiftMinutes = differenceInMinutes(clockOutTime, clockInTime);
    const breakStartAtMinutes = Math.floor(shiftMinutes / 2) - 15;
    breakStartTime = addMinutes(clockInTime, breakStartAtMinutes);
  }

  return {
    clockIn: clockInTime,
    clockOut: clockOutTime,
    breakStart: breakStartTime,
    breakEnd: breakStartTime ? addMinutes(breakStartTime, 30) : undefined,
  };
}

// Utility function to validate if a given time is between a list of before and after times
// Used to validate that break times are within the clock in/out or break times
export function validateTimeIsBetween(
  time: Date,
  listBefore: Array<Date | undefined>,
  listAfter: Array<Date | undefined>
): boolean {
  for (const beforeTime of listBefore) {
    if (isDefined(beforeTime) && time < beforeTime) {
      return false;
    }
  }

  for (const afterTime of listAfter) {
    if (isDefined(afterTime) && time > afterTime) {
      return false;
    }
  }

  return true;
}

/*
 * Utility function to fix shift times by setting the year, month, and day
 * of clock in, break start, break end, and clock out times to match the shift start date.
 * It also ensures that each time is after the previous time.
 *
 * Example:
 * If the shift starts on 2023-10-01 and the clock in time is 2023-09-30 08:00,
 * the function will adjust the clock in time to 2023-10-01 08:00.
 *
 * If the clock in time is at 2023-10-01 08:00 PM and the break start time is at 2023-10-01 02:00 AM,
 * the function will adjust the break start time to 2023-10-02 02:00 AM
 * to ensure it is after the clock in time.
 * Same happens for break end and clock out times.
 */
export function fixDayForShiftClockTimes(props: {
  shiftStart: Date;
  clockTimes: {
    clockIn: Date | undefined;
    breakStart: Date | undefined;
    breakEnd: Date | undefined;
    clockOut: Date | undefined;
  };
}): {
  clockIn: Date | undefined;
  breakStart: Date | undefined;
  breakEnd: Date | undefined;
  clockOut: Date | undefined;
} {
  const { shiftStart, clockTimes } = props;
  const { clockIn, breakStart, breakEnd, clockOut } = clockTimes;

  // Make sure clock times are adjusted to the shift day
  const sameDayTimes = [clockIn, breakStart, breakEnd, clockOut].map((time) => {
    if (!isDefined(time)) {
      return time;
    }

    let fixedTime = setYear(time, shiftStart.getFullYear());
    fixedTime = setMonth(fixedTime, shiftStart.getMonth());
    fixedTime = setDate(fixedTime, shiftStart.getDate());
    return fixedTime;
  });

  // Ensure that each time is after the previous time
  const fixedTimes = sameDayTimes.map((time, timeIndex) => {
    if (!isDefined(time)) {
      return time;
    }

    // If current time seems to be next day after a previous time, add one day to it
    for (let index = 0; index < timeIndex; index += 1) {
      if (isDefined(sameDayTimes[index])) {
        if (sameDayTimes[index]! > time) {
          return addDays(time, 1);
        }

        break;
      }
    }

    return time;
  });

  return {
    clockIn: fixedTimes[0],
    breakStart: fixedTimes[1],
    breakEnd: fixedTimes[2],
    clockOut: fixedTimes[3],
  };
}
